import { type FormEvent, useRef, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import BackIcon from "../../components/back-icon";
import { Label } from "../../components/ui/label";
import { useGetOTP } from "../../lib/hooks/auth/use-get-otp";
import { useToast } from "../../components/ui/use-toast";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../components/ui/input-otp";
import { useVerifyOTP, useFetchJWT } from "../../lib/hooks/auth/use-verify-otp";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "../../contexts/auth-context";
import Logo from "../../components/logo";
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { getCountryCallingCode } from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';


// Custom CSS for styling the PhoneInput and country dropdown
const phoneInputStyles = `
  .PhoneInput {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    transition: all 0.2s ease;
  }
  .PhoneInput:hover {
    border-color: #d1d5db;
  }
  .PhoneInput:focus-within {
    border-color: #6b7280;
    box-shadow: 0 0 0 2px rgba(107, 114, 128, 0.2);
  }
  .PhoneInputInput {
    flex: 1;
    border: none;
    outline: none;
    font-size: 0.875rem;
    line-height: 1.5;
    color: #111827;
    background: transparent;
  }
  .PhoneInputCountry {
    position: relative;
  }
  .PhoneInputCountrySelect {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background: transparent;
  border: none;
  padding-right: 1.5rem; /* leave space for custom arrow */
  padding-left: 0.25rem;  /* reduce left padding so arrow doesn't overlap flag */
  cursor: pointer;
}


  .PhoneInputCountrySelect:hover {
    background: #f3f4f6;
  }
  .PhoneInputCountrySelect:focus {
    outline: none;
    border-color: #6b7280;
    box-shadow: 0 0 0 2px rgba(107, 114, 128, 0.2);
  }

  @media (max-width: 640px) {
    .PhoneInput {
      padding: 0.5rem 0.75rem;
    }
    .PhoneInputInput {
      font-size: 0.75rem;
    }
    .PhoneInputCountrySelect {
      padding: 0.25rem 1.25rem 0.25rem 0.5rem;
      font-size: 0.75rem;
    }
  }
`;

// Define the VerifyPatient component
function VerifyPatient() {
  const { toast } = useToast();
  const { setMobile, setToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get("redirect") ?? "/doctors";
  const [mobile, setMobileVal] = useState<string | undefined>(undefined);
  const isMobileValid = mobile ? isValidPhoneNumber(mobile) : false;
  const [otp, setOtp] = useState("");
  const [otpSession, setOtpSession] = useState(false);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [timer, setTimer] = useState(60);
  const otpInputRef = useRef<HTMLInputElement>(null);
  const { getOTP, data, isPending: isGettingOTP } = useGetOTP();
  const { verifyOTP, isPending: isVerifying } = useVerifyOTP();
  const { fetchJWT, isPending: isFetchingJWT } = useFetchJWT();

  // Reset OTP state when mobile number is invalid
  useEffect(() => {
    if (!isMobileValid) {
      setOtp("");
      setOtpSession(false);
    }
  }, [mobile]);

  // Manage the resend OTP timer
  useEffect(() => {
    if (isResendDisabled && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
    if (timer === 0) {
      setIsResendDisabled(false);
    }
  }, [timer, isResendDisabled]);

  // Handle OTP request
  function handleGetOTP() {
    if (isMobileValid) {
      getOTP(mobile!, {
        onSuccess: () => {
          setOtpSession(true);
          // Focus the first OTP input field if available
          setTimeout(() => {
            const firstInput = document.querySelector('input[aria-label="Input OTP slot 0"]') as HTMLInputElement;
            if (firstInput) {
              firstInput.focus();
            }
          }, 100);
          setIsResendDisabled(true);
          setTimer(60);
        },
      });
    } else {
      toast({ title: "Please enter a valid mobile number" });
    }
  }

  // Handle OTP verification
  function handleVerifyOTP() {
    if (otp.length === 4) {
      verifyOTP(
        { otp, requestId: data?.requestId! },
        {
          onSuccess: (data) => {
            localStorage.setItem("mobile", mobile!);
            setMobile(mobile!);
            setToken(data.token, data.refreshToken);
            navigate(redirectUrl);
            toast({ title: "Mobile number verified" });
          },
        }
      );
    } else {
      toast({ title: "Please enter a valid OTP" });
    }
  }

  // Handle form submission
  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!otpSession) handleGetOTP();
    else handleVerifyOTP();
  }

  return (
    <>
      <style>{phoneInputStyles}</style>
      <nav className="sticky top-0 z-10 flex w-full items-center justify-start gap-4 px-6 py-4 border-b bg-white/95 backdrop-blur-sm shadow-sm">
        <Logo />
      </nav>
      <div className="container flex min-h-[calc(100vh-73px)] max-w-md flex-col py-6 sm:justify-center bg-gradient-to-b from-gray-50 to-white">
        <div className="mb-6 flex items-center gap-3">
          <Button
            size="icon"
            variant="outline"
            onClick={() => navigate(-1)}
            className="relative group h-10 w-10 rounded-full border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-all"
          >
            <BackIcon />
            <span className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:flex items-center px-2 py-1 rounded text-xs text-white bg-gray-800 shadow-lg z-10">
              Go Back
            </span>
          </Button>
          <h1 className="text-xl font-bold text-gray-800">Verify mobile number</h1>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="flex justify-center mb-6">
            <div className="bg-gray-50 p-3 rounded-full">
              <ShieldCheck className="h-10 w-10 text-gray-600" />
            </div>
          </div>
          <p className="text-center text-gray-600 mb-6">
            We'll send a verification code to your mobile number to confirm your identity
          </p>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Enter Whatsapp Number</Label>
              <PhoneInput
                international
                defaultCountry="IN"
                value={mobile}
                onChange={setMobileVal}
                placeholder="Enter phone number"
                className="PhoneInput"
                countryCallingCodeEditable={false}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  const input = e.target as HTMLInputElement;
                  const dialCode = '+' + getCountryCallingCode('IN'); 
                  const dialCodeLength = dialCode.length;
                  const selectionStart = input.selectionStart;
                  if (e.key === 'Backspace' && selectionStart !== null && selectionStart <= dialCodeLength) {
                    e.preventDefault();
                  }
                }}
              />
            </div>

            {otpSession && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Verification Code</Label>
                <InputOTP
                  maxLength={4}
                  disabled={!otpSession}
                  value={otp}
                  onChange={(val) => setOtp(val)}
                  className="gap-3"
                >
                  <InputOTPGroup className="w-full gap-3">
                    <InputOTPSlot
                      className="w-full h-14 rounded-lg border-gray-200 focus:border-gray-300 focus:ring-2 focus:ring-gray-100 text-xl"
                      index={0}
                    />
                    <InputOTPSlot
                      className="w-full h-14 rounded-lg border-gray-200 focus:border-gray-300 focus:ring-2 focus:ring-gray-100 text-xl"
                      index={1}
                    />
                    <InputOTPSlot
                      className="w-full h-14 rounded-lg border-gray-200 focus:border-gray-300 focus:ring-2 focus:ring-gray-100 text-xl"
                      index={2}
                    />
                    <InputOTPSlot
                      className="w-full h-14 rounded-lg border-gray-200 focus:border-gray-300 focus:ring-2 focus:ring-gray-100 text-xl"
                      index={3}
                    />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            )}

            {otpSession && (
              <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <span className="text-sm text-gray-600">
                  {isResendDisabled ? `Didn't receive OTP? ${timer}s` : "You can resend OTP now"}
                </span>
                <Button
                  type="button"
                  variant="link"
                  disabled={isResendDisabled}
                  onClick={handleGetOTP}
                  className="text-gray-700 font-medium"
                >
                  Resend OTP
                </Button>
              </div>
            )}

            <div className="bottom-5 left-0 w-full space-y-4 max-sm:fixed max-sm:px-4">
              <Button
                type="submit"
                className="w-full font-bold"
                disabled={
                  (otpSession ? otp.length !== 4 : !isMobileValid) ||
                  isGettingOTP ||
                  isVerifying
                }
              >
                {(isGettingOTP || isVerifying || isFetchingJWT) && (
                  <Loader2 className="mr-2 size-4 animate-spin" strokeWidth={3} />
                )}
                {!data ? "Get OTP" : "Verify"}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default VerifyPatient;